import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  getSensors,
  getSensorConnectivity,
  getSensorPresence,
  getSensorSessions,
  getPCOStateChanges,
  countAlertTransitions,
  countAlarmShutoffs,
} from "../unitEventsUtils";
import { getScopedSensorAlarms } from "../scopedTelemetry";
import { useSensorTenures } from "./useSensorTenure";
import {
  scopeKeyOf,
  TENURE_UNRESOLVED,
  type TenureScope,
} from "../../../services/installationTenure";
import { computeBuildingTrend } from "../trendUtils";
import { deriveWellnessAlerts, type WellnessAlert } from "../wellnessAttention";
import { parseUtc } from "../../../services/parseUtc";

export type AggregateRow = {
  unitsCount: number | null;
  onlineCount: number | null;
  alertsCount: number | null;
  shutdownsCount: number | null;
  hasPCO: boolean;
  /** Aggregate safety trend across the building's sensors (alarm-based). */
  trendStatus: string | null;
  trendRank: number | null;
  /** Graded signed trend level -3..+3 (positive = worsening). */
  trendLevel: number | null;
  /** 0-100 "most problematic" priority (severity blended with decline slope). */
  priorityScore: number | null;
  priorityBand: string | null;
  /** Highest single-sensor priority in the building, for tie-breaking/secondary display. */
  worstUnitPriority: number | null;
  aggStatus: "loading" | "ready" | "error";
};

export type PortfolioTotals = {
  properties: number;
  units: number;
  onlineSensors: number;
  pcoEquipped: number;
  alerts: number;
  shutdowns: number;
};

export type BuildingAggregatesResult = {
  aggregates: Map<string, AggregateRow>;
  /** Flat list of every alarm record across every sensor, for charting. Clipped
   *  to each sensor's current location, so a moved sensor no longer draws its
   *  old kitchen's alerts onto this portfolio's chart. */
  allAlertRecords: any[];
  totals: PortfolioTotals;
  /**
   * Wellness alerts per building, for the attention feed.
   *
   * Derived here rather than per property because the portfolio page is where
   * an operator watching forty residents actually starts, and a resident
   * nobody has seen is the one thing in this feed that gets worse while you
   * are not looking at it.
   */
  wellnessByBuilding: Record<string, WellnessAlert[]>;
};

type Building = {
  buildingId: string;
  name: string;
  LatLong: [number, number];
};

const ALERT_PAGE_SIZE = 5000;
const REFETCH_INTERVAL = 60_000 * 60; // 1h, matches Sensors view
// Navigations within STALE_TIME reuse the cache (no refetch on remount); GC_TIME
// keeps that cache alive while the user is off the dashboard, so coming back is
// instant rather than a cold reload.
const STALE_TIME = 10 * 60_000; // 10m
const GC_TIME = 2 * 60 * 60_000; // 2h
// 10+ offline sensors saturates the offline weight regardless of property size.
const OFFLINE_COUNT_REF = 10;

/**
 * Fan-out per-building → fetch sensor lists, then per-sensor session totals
 * within the chosen date window, then aggregate into Units + Alerts counts.
 *
 * Returns a `Map<buildingId, AggregateRow>` whose reference is stable across
 * renders unless a query's data actually changed (uses `dataUpdatedAt` +
 * `status` as the memo signature).
 */
export function useBuildingAggregates(
  buildings: Building[],
  startDate: string | null,
  endDate: string | null
): BuildingAggregatesResult {
  // Step 1: sensors per building.
  const sensorQueries = useQueries({
    queries: buildings.map((b) => ({
      queryKey: ["sensors", b.buildingId],
      queryFn: async () => await getSensors(b.buildingId),
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchInterval: REFETCH_INTERVAL,
    })),
  });

  // Flatten sensors so we can fan-out one session query per sensor.
  type SensorRef = { buildingId: string; name: string };
  const sensorRefs: SensorRef[] = [];
  buildings.forEach((b, i) => {
    const q = sensorQueries[i];
    const list = q?.data?.data ?? [];
    list.forEach((s: { name: string }) => {
      if (s?.name) sensorRefs.push({ buildingId: b.buildingId, name: s.name });
    });
  });

  // One installations read per sensor, shared with the property table, the
  // sensor detail page and both report hooks. The alert fan-out is gated on it.
  const tenures = useSensorTenures(sensorRefs.map((s) => s.name));
  const scopeOf = (name: string): TenureScope =>
    tenures.get(name)?.scope ?? TENURE_UNRESOLVED;
  const settledFor = (name: string): boolean =>
    tenures.get(name)?.settled ?? false;

  // Step 2: alert history per sensor. The alarms_v2 endpoint doesn't accept a
  // date filter, so we cache one fetch per sensor and filter client-side --
  // which now also means clipping it to the sensor's current location, so a
  // moved sensor stops carrying its old kitchen's alerts into this building's
  // totals. Live state (online now) reads the unclipped rows; see liveRows.
  const sessionQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["alerts-agg", s.name, scopeKeyOf(scopeOf(s.name))],
      queryFn: async () =>
        await getScopedSensorAlarms(s.name, ALERT_PAGE_SIZE, scopeOf(s.name)),
      enabled: !!s.name && settledFor(s.name),
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      refetchInterval: REFETCH_INTERVAL,
    })),
  });

  // Step 3: PCO state + connectivity per sensor, matching the Sensors page
  // "stove control" logic: a sensor is "controllable" only when it's currently
  // online AND its latest PCO state reports the unit connected. Building-level
  // hasPCO is true when at least one sensor at the building meets that bar.
  const pcoQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["pco-presence", s.name],
      // LIVE-STATE EXEMPT: newest row only, answering whether a power cutoff
      // unit is attached right now. Clipping it would drop the attachment for a
      // sensor re-installed minutes ago and under-report the fleet's equipment.
      queryFn: async () => await getPCOStateChanges(s.name, 1),
      enabled: !!s.name,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
    })),
  });
  const connectivityQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["connectivity-presence", s.name],
      // LIVE-STATE EXEMPT: newest row only, answering online-or-offline right
      // now. A sensor moved an hour ago has no post-install rows yet and would
      // read as offline, which on a safety dashboard is a false alarm.
      queryFn: async () => await getSensorConnectivity(s.name, 1),
      enabled: !!s.name,
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
    })),
  });

  // Last presence and last cooking session per sensor, one row each.
  //
  // Query keys and staleTime deliberately match the property table's, so
  // arriving there from here is a cache hit rather than a second fan-out over
  // the same sensors. Both are pageSize=1: the newest row is all the wellness
  // rule needs, so this costs far less than the alarm history above.
  const presenceQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["last-presence", s.name],
      // LIVE-STATE EXEMPT: when was this resident last seen at all. The wellness
      // rule gets worse the longer the answer is, so the last sighting matters
      // whichever kitchen it happened in -- clipping it would reset the clock.
      queryFn: async () => await getSensorPresence(s.name, 1),
      enabled: !!s.name,
      staleTime: 60_000,
    })),
  });
  const lastSessionQueries = useQueries({
    queries: sensorRefs.map((s) => ({
      queryKey: ["last-session-row", s.name],
      // LIVE-STATE EXEMPT: when did anyone last cook, for the same wellness
      // reason as presence above. This is a timestamp, not a count, and a move
      // does not make the last meal un-happen.
      queryFn: async () => await getSensorSessions(s.name, 1, null, null),
      enabled: !!s.name,
      staleTime: 60_000,
    })),
  });

  // Memo signatures: these only change when query data/status actually
  // changes, so the returned Map is stable across no-op renders.
  const sensorSig = sensorQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const sessionSig = sessionQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const pcoSig = pcoQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const connSig = connectivityQueries
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const wellnessSig = presenceQueries
    .concat(lastSessionQueries)
    .map((q) => `${q.status}:${q.dataUpdatedAt ?? 0}`)
    .join("|");
  const buildingSig = buildings.map((b) => b.buildingId).join("|");
  // Cutoffs move -- a tenure resolves, a sensor is re-homed -- and every count
  // below depends on them, so they belong in the signature.
  const tenureSig = sensorRefs
    .map((s) => `${s.name}:${scopeKeyOf(scopeOf(s.name))}:${settledFor(s.name)}`)
    .join("|");

  return useMemo(() => {
    const result = new Map<string, AggregateRow>();
    const allAlertRecords: any[] = [];
    sessionQueries.forEach((q) => {
      const records = q?.data?.data;
      if (Array.isArray(records)) allAlertRecords.push(...records);
    });

    const wellnessByBuilding: Record<string, WellnessAlert[]> = {};

    let onlineSensors = 0;
    let pcoEquipped = 0;
    sensorRefs.forEach((ref, idx) => {
      const conn = connectivityQueries[idx]?.data?.data?.[0];
      const pco = pcoQueries[idx]?.data?.data?.[0];
      const online = conn?.eventType === "connected";
      const pcoConnected = pco?.currentUnitConnectedStatus === 1;

      // Same shape and same fallbacks the property table uses, so one sensor
      // cannot be flagged on one screen and not the other. dateCreated wins
      // over the event's own timestamp because a sensor whose clock is wrong
      // still arrives at a known moment.
      const presenceRow = presenceQueries[idx]?.data?.data?.[0];
      const sessionRow = lastSessionQueries[idx]?.data?.data?.[0];
      const alerts = deriveWellnessAlerts({
        scsName: ref.name,
        lastPresenceAt:
          parseUtc(presenceRow?.dateCreated) ??
          parseUtc(presenceRow?.datetime),
        lastCookedAt:
          parseUtc(sessionRow?.dateCreated) ?? parseUtc(sessionRow?.dateStart),
        // An offline sensor is not a quiet kitchen. deriveWellnessAlerts
        // refuses to claim anything about the resident when this is false.
        inContact: online,
      });
      if (alerts.length > 0) {
        const bucket = wellnessByBuilding[ref.buildingId] ?? [];
        bucket.push(...alerts);
        wellnessByBuilding[ref.buildingId] = bucket;
      }

      if (online) onlineSensors += 1;
      // PCO presence is known from its last reported state, so count it even when
      // the sensor is offline, since shutdown history is still meaningful.
      if (pcoConnected) pcoEquipped += 1;
    });

    buildings.forEach((b, i) => {
      const sensorsQ = sensorQueries[i];
      if (!sensorsQ) {
        result.set(b.buildingId, {
          unitsCount: null,
          onlineCount: null,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO: false,
          trendStatus: null,
          trendRank: null,
          trendLevel: null,
          priorityScore: null,
          priorityBand: null,
          worstUnitPriority: null,
          aggStatus: "loading",
        });
        return;
      }
      if (sensorsQ.isError) {
        result.set(b.buildingId, {
          unitsCount: null,
          onlineCount: null,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO: false,
          trendStatus: null,
          trendRank: null,
          trendLevel: null,
          priorityScore: null,
          priorityBand: null,
          worstUnitPriority: null,
          aggStatus: "error",
        });
        return;
      }
      if (sensorsQ.isPending) {
        result.set(b.buildingId, {
          unitsCount: null,
          onlineCount: null,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO: false,
          trendStatus: null,
          trendRank: null,
          trendLevel: null,
          priorityScore: null,
          priorityBand: null,
          worstUnitPriority: null,
          aggStatus: "loading",
        });
        return;
      }

      const sensorList: { name: string }[] = sensorsQ.data?.data ?? [];
      const unitsCount = sensorList.length;

      const sessionIdxs: number[] = [];
      sensorRefs.forEach((s, idx) => {
        if (s.buildingId === b.buildingId) sessionIdxs.push(idx);
      });

      // Online sensor count for this building (null until connectivity loads).
      const buildingConnLoadQs = sessionIdxs.map((i2) => connectivityQueries[i2]);
      // Only "loading" before the first result arrives (isPending). A background
      // refetch keeps the cached value visible instead of flashing to loading.
      const connLoading = buildingConnLoadQs.some((q) => q?.isPending);
      const connErrored = buildingConnLoadQs.some((q) => q?.isError);
      const onlineCount =
        connLoading || connErrored
          ? null
          : buildingConnLoadQs.reduce(
              (acc, q) =>
                acc +
                (q?.data?.data?.[0]?.eventType === "connected" ? 1 : 0),
              0
            );

      if (unitsCount === 0) {
        result.set(b.buildingId, {
          unitsCount: 0,
          onlineCount: 0,
          alertsCount: 0,
          shutdownsCount: 0,
          hasPCO: false,
          trendStatus: null,
          trendRank: null,
          trendLevel: 0,
          priorityScore: 0,
          priorityBand: "none",
          worstUnitPriority: 0,
          aggStatus: "ready",
        });
        return;
      }

      const buildingPcoQs = sessionIdxs.map((i2) => pcoQueries[i2]);
      // A property "has a PCO" if any sensor's last reported PCO state shows one
      // connected, regardless of current connectivity, so shutdown counts show
      // (including 0) instead of N/A when PCO sensors are offline.
      const hasPCO = sessionIdxs.some((_, idxInBuilding) => {
        const pco = buildingPcoQs[idxInBuilding]?.data?.data?.[0];
        return pco?.currentUnitConnectedStatus === 1;
      });

      const buildingSessionQs = sessionIdxs.map((i2) => sessionQueries[i2]);
      const anyError = buildingSessionQs.some((q) => q?.isError);
      const anyLoading = buildingSessionQs.some((q) => q?.isPending);

      // Building trend is alarm-based and uses its own 4-week windows, so it's
      // ready as soon as the per-sensor alarm history loads, independent of the
      // date picker (unlike alertsCount/shutdownsCount).
      // Offline weight blends the SHARE of sensors down with the absolute COUNT,
      // so a small property with half its sensors dark ranks up (share), and a
      // handful of offline sensors still registers in a large property (count).
      const offlineSensors =
        onlineCount != null ? Math.max(0, unitsCount - onlineCount) : 0;
      const offlineNorm =
        onlineCount != null && unitsCount > 0
          ? Math.max(
              offlineSensors / unitsCount,
              Math.min(offlineSensors / OFFLINE_COUNT_REF, 1)
            )
          : 0;
      const trend =
        !anyError && !anyLoading
          ? computeBuildingTrend(
              buildingSessionQs.map((q) => q?.data?.data ?? []),
              new Date(),
              {
                offlineNorm,
                // Aligned with the arrays above. A sensor installed here last
                // week has no prior window, and summing its empty prior against
                // its real recent would tilt the whole property toward
                // "Worsening" on the strength of a move.
                observedSinceList: sessionIdxs.map(
                  (i2) => scopeOf(sensorRefs[i2].name).cutoffMs
                ),
              }
            )
          : null;

      if (anyError) {
        result.set(b.buildingId, {
          unitsCount,
          onlineCount,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO,
          trendStatus: null,
          trendRank: null,
          trendLevel: null,
          priorityScore: null,
          priorityBand: null,
          worstUnitPriority: null,
          aggStatus: "error",
        });
        return;
      }
      if (!startDate || !endDate || anyLoading) {
        result.set(b.buildingId, {
          unitsCount,
          onlineCount,
          alertsCount: null,
          shutdownsCount: null,
          hasPCO,
          trendStatus: trend?.status ?? null,
          trendRank: trend?.rank ?? null,
          trendLevel: trend?.level ?? null,
          priorityScore: trend?.priorityScore ?? null,
          priorityBand: trend?.priorityBand ?? null,
          worstUnitPriority: trend?.worstUnitPriority ?? null,
          aggStatus: "loading",
        });
        return;
      }

      let alertsCount = 0;
      let shutdownsCount = 0;
      buildingSessionQs.forEach((q) => {
        const records = q?.data?.data ?? [];
        alertsCount += countAlertTransitions(records, startDate, endDate);
        shutdownsCount += countAlarmShutoffs(records, startDate, endDate);
      });

      result.set(b.buildingId, {
        unitsCount,
        onlineCount,
        alertsCount,
        shutdownsCount,
        hasPCO,
        trendStatus: trend?.status ?? null,
        trendRank: trend?.rank ?? null,
        trendLevel: trend?.level ?? null,
        priorityScore: trend?.priorityScore ?? null,
        priorityBand: trend?.priorityBand ?? null,
        worstUnitPriority: trend?.worstUnitPriority ?? null,
        aggStatus: "ready",
      });
    });

    let totalUnits = 0;
    let totalAlerts = 0;
    let totalShutdowns = 0;
    result.forEach((row) => {
      totalUnits += row.unitsCount ?? 0;
      totalAlerts += row.alertsCount ?? 0;
      totalShutdowns += row.shutdownsCount ?? 0;
    });

    return {
      aggregates: result,
      allAlertRecords,
      wellnessByBuilding,
      totals: {
        properties: buildings.length,
        units: totalUnits,
        onlineSensors,
        pcoEquipped,
        alerts: totalAlerts,
        shutdowns: totalShutdowns,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    buildingSig,
    sensorSig,
    sessionSig,
    pcoSig,
    connSig,
    wellnessSig,
    tenureSig,
    startDate,
    endDate,
  ]);
}
